import React, { type JSX } from "react";
import { Field, PeopleTable, formatArray } from "./DealFieldPrimitives";
import type { DealData } from "./dealTypes";

/** Sector, year, country and stage — present on every deal. */
function CoreFacts({ deal }: { deal: DealData }): JSX.Element {
  return (
    <>
      <Field label="Sector">{deal.sector || "--"}</Field>
      <Field label="Investment Year">{deal.investmentYear || deal.year || "--"}</Field>
      <Field label="Country">{deal.country || "--"}</Field>
      <Field label="Stage">{deal.dealStage || deal.stage || "--"}</Field>
    </>
  );
}

/** Dates, contacts and lifecycle fields, each shown only where populated. */
function LifecycleFacts({ deal }: { deal: DealData }): JSX.Element {
  const normalizedStage = (deal.dealStage || deal.stage || "").toLowerCase();
  const contacts = [deal.efrontIoContact1, deal.efrontIoContact2].filter(Boolean);

  return (
    <>
      {deal.closingDate && <Field label="Close Year">{deal.closingDate.substring(0, 4)}</Field>}
      {deal.exitYear && <Field label="Exit Year">{deal.exitYear}</Field>}
      {deal.lastIcStage && <Field label="Last IC Stage">{deal.lastIcStage}</Field>}
      {contacts.length > 0 && (
        <Field label="Deal Contacts">
          <PeopleTable items={contacts} />
        </Field>
      )}
      <Field label="Team">{formatArray(deal.team)}</Field>
      {deal.deadReason && <Field label="Dead Reason">{deal.deadReason}</Field>}
      {normalizedStage === "dead" && deal.lastActiveStatus && (
        <Field label="Last Active Stage">{deal.lastActiveStatus}</Field>
      )}
      {normalizedStage === "exited" && deal.exitCompleted && (
        <Field label="Exit Completed">{deal.exitCompleted}</Field>
      )}
    </>
  );
}

/** Long-form narrative fields below the divider. */
function AdditionalDetails({ deal }: { deal: DealData }): JSX.Element {
  const overview =
    deal.companyOverview || deal.company_overview || deal.COMPANY_OVERVIEW || "--";

  return (
    <dl className="space-y-4">
      <Field
        label="Business Overview"
        valueClass="text-base text-gray-700 whitespace-pre-wrap leading-relaxed"
      >
        {overview}
      </Field>
      <Field label="Vendor">{formatArray(deal.vendor)}</Field>
      <Field label="People" spacedLabel>
        <PeopleTable items={deal.people} />
      </Field>
      <Field label="Advisors" spacedLabel>
        <PeopleTable items={deal.advisors} />
      </Field>
    </dl>
  );
}

/** The General tab of the deal details modal. */
export default function DealGeneralTab({ deal }: { deal: DealData }): JSX.Element {
  // SENSITIVITY is a varchar column holding 'TRUE'/'FALSE', returned lowercase
  const isSensitive = deal.sensitivity?.toLowerCase() === "true";

  return (
    <div className="space-y-6">
      <div>
        <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">Company Name</dt>
        <dd className="mt-1 text-lg text-gray-900 font-semibold">
          {isSensitive ? "Sensitive" : deal.portfolioCompanyName || "--"}
        </dd>
      </div>

      <Field label="Description">{deal.business || "No description"}</Field>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <CoreFacts deal={deal} />
        <LifecycleFacts deal={deal} />
      </dl>

      <div className="border-t border-gray-200 my-6"></div>

      <AdditionalDetails deal={deal} />
    </div>
  );
}
